import 'jquery-migrate';
import $ from 'jquery';
import Hubs from '../core';
import HubsEmbed from './core';
import Shared from '../shared_util';

/**
 * HubsEmbed Application
 *
 * @class LazyLoader
 * @classdesc Application LazyLoader for Infinite Scrolling on Collection Pages
 * @namespace HubsEmbed
 * @param {Object} [options] Configuration Settings for the Application
 * @return {Object} The Class Instance
 * @constructor
 */
HubsEmbed.LazyLoader = function(app) {
    
    /* **************************************************************************************** */
    /* * Private Methods/Members Declarations                                                 * */
    /* **************************************************************************************** */
    var initialize = null,
        initEvents = null;


    /* **************************************************************************************** */
    /* * Public Properties                                                                    * */
    /* **************************************************************************************** */

    /** @member {Object} app A reference to the Application Object */
    this.app              = app;
    
    /** @member {Object} $container A jQuery Element Handle to the Container DIV of the LazyLoader  */
    this.$container       = null;
    
    /** @member {Object} $loadingNotifier A jQuery Element Handle to the Container DIV of the Loading Notifier Box */
    this.$loadingNotifier = null;
    
    /** @member {Object} $scrollContainer A jQuery Element Handle to the Container DIV that Scrolls (Window or Other) */
    this.$scrollContainer = null;
    
    /** @member {Number} collectionId The ID of the Collection currently being viewed */
    this.collectionId     = 0;
    
    /** @member {Number} containerOffset The Top Offset of the Container */
    this.containerOffset  = 0;
    
    /** @member {Number} itemSize The Outer Width or Height of an Item in the List */
    this.itemSize         = 0;
    
    /** @member {Number} itemCount The Number of Items loaded in the List */
    this.itemCount        = 0;
    
    /** @member {Number} viewportSize The Width or Height of the Viewport  */
    this.viewportSize     = 0;
    
    /** @member {Number} lastItemBounds The X or Y Boundary of the Last Item in the List  */
    this.lastItemBounds   = 0;
    
    /** @member {Number} currentPage The Current Page Displayed within the Collection */
    this.currentPage      = 0;
    
    /** @member {Boolean} loadingItems A flag indicating whether Items are currently being loaded or not */
    this.loadingItems     = false;
    
    /** @member {String} loadUrl The URL to Load Items from (Initial Load and Subsequent Loads have different URLs) */
    this.loadUrl          = null;
    
    /** @member {Boolean} endOfCollection A flag indicating whether we have reached the end of the collection */
    this.endOfCollection  = false;
    

    /* **************************************************************************************** */
    /* * Private Methods/Members Definitions                                                  * */
    /* **************************************************************************************** */
    /**
     * Initialize the Control
     *
     * @private
     * @this HubsEmbed.LazyLoader
     * @return undefined
     * @constructs
     */
    initialize = $.proxy(function() {
        // Debug Statement
        HubsEmbed.log('lazy-loader-init');
        
        // Store Reference to Hubs.App
        this.app = app;
        
        // Get Handles to Container & Loading-Notifier Elements
        this.$container = this.app.$appContainer.find(this.app.itemContainerSelector);
        this.$loadingNotifier = this.app.$appContainer.find(this.app.config.lazyloader.elements.notify);
        if (!this.$container.length) { return; }
        
        // Set Default Scroll Container
        this.$scrollContainer = Shared.$('window');
        
        // Get Collection ID
        this.collectionId = this.app.config.collection;
        
        // Set Initial Load URL
        this.loadUrl = this.app.config.serverUrl.initialLoad;
        
        // Store Top Offset of Container
        this.containerOffset = this.app.$appContainer[0].offsetTop;
        
        // Initialize Events
        initEvents();
        
        // Debug Statement
        HubsEmbed.log('lazy-loader-init-end');
    }, this);
    
    /**
     * Initialize Events for the HubsEmbed LazyLoader
     *  
     * @private 
     * @this HubsEmbed.LazyLoader
     * @return undefined
     */
    initEvents = $.proxy(function() {
        // Debug Statement
        HubsEmbed.log('lazy-loader-events-init');
        
        // Window Scroll Event
        Shared.$('window')
            .on('debouncedresize.HubsEmbed.LazyLoader', $.proxy(this.resizeEvent, this))
            .on('scroll.HubsEmbed.LazyLoader', $.proxy(this.scrollEvent, this));
        
        this.app.$appContainer.parent()
            .on('scroll.HubsEmbed.LazyLoader', $.proxy(this.scrollEvent, this));
    }, this);
    
    
    /* **************************************************************************************** */
    /* * Entry Point                                                                          * */
    /* **************************************************************************************** */
    initialize();
};
// End of HubsEmbed.LazyLoader


/* ******************************************************************************************** */
/* * Public Methods                                                                           * */
/* ******************************************************************************************** */

/**
 * Observes and Responds to the Resize Events fired on the Global Window Object
 * 
 * @public
 * @this HubsEmbed.LazyLoader
 * @param {Object} eventData The Event Data associated with the Event 
 * @return {Boolean} The success state of the event; determines whether to allow event-bubbling
 */
HubsEmbed.LazyLoader.prototype.resizeEvent = function(eventData) {
    // Debug Statement
    HubsEmbed.log('lazy-loader-resize');
    
    // Update Carousel
    HubsEmbed.root.setTimeout($.proxy(this.updateSizes, this), 20); // just after app resize event
    return true;
};

/**
 * Observes and Responds to the Scroll Events fired on the Global Window Object
 * 
 * @public
 * @this HubsEmbed.LazyLoader
 * @param {Object} eventData The Event Data associated with the Event 
 * @return {Boolean} The success state of the event; determines whether to allow event-bubbling
 */
HubsEmbed.LazyLoader.prototype.scrollEvent = function(eventData) {
    // Check for More Pages
    if (this.loadingItems) { return; }
    
    // Debug Statement
    //HubsEmbed.log('lazy-loader-scroll');
    
    // Get Handle to Scroll Container
    if (eventData.currentTarget === HubsEmbed.root) {
        this.$scrollContainer = Shared.$('window');
    } else {
        this.$scrollContainer = this.app.$appContainer.parent();
    } 
    
    // Update Sizes to get Last Item Bounds 
    this.updateSizes();

    // Check Boundary to Load More Items
    this.checkBoundary(false);
    return true;
};

HubsEmbed.LazyLoader.prototype.updateTileCount = function() {
    var count = this.$container.find('.tile').not('#loading-notifier').length;
    this.app.$appContainer.attr('data-tile-count', count);
    return count;
}
/**
 * Loads the next set of items via Ajax and populates the page with new items
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.loadItems = function(initialLoad, callback) {
    var data = '', disableTracking = this.app.config.enablePageTracking ? '0' : '1', 
        requestItemCount = 0;
    
    // Limit Maximum Number of Returned Items
    if (this.app.config.lazyloader.maxItemsTotal > 0) {
        if (this.itemCount >= this.app.config.lazyloader.maxItemsTotal) {
            this.endOfCollection = true;
        } else {
            if (this.itemCount + this.app.config.lazyloader.itemDisplayLimit > this.app.config.lazyloader.maxItemsTotal) {
                requestItemCount = this.app.config.lazyloader.maxItemsTotal - this.itemCount;
            }
        }
    }
    
    // Data to Request Next Set of Items
    data = this.collectionId + '/' + (this.currentPage + 1) + '/' + this.app.config.lazyloader.itemDisplayLimit + '/' + requestItemCount + '/';
    if (this.loadUrl === this.app.config.serverUrl.initialLoad) {
        data += disableTracking + '/';
    }
    data += this.app.uuid;
    
    if (this.loadingItems || this.endOfCollection) { return; }
    
    // Update Loading Flag
    this.setLoading(true);
    
    // Check to see if we are previewing
    if (this.app.config.previewOn) {
        data += '?ignoreLockForPreview=1';
    }
    
    try {
        Shared.insertJS(this.loadUrl + data, $.proxy(function() {
            var emptyHub = '',
                itemData = HubsEmbed.root['ehr-' + this.app.uuid][HubsEmbed.root['ehr-' + this.app.uuid].length - 1];
            
            // Clean up Item Data
            itemData.html = Shared.trim(itemData.html);
                
            // Check for Empty Hub
            if (itemData.invalid || (!itemData.html.length && itemData.initial)) {
                // Update Loading Flag
                this.setLoading(false);
                this.endOfCollection = true;
                
                // Display Notice to User
                emptyHub  = '<div class="empty-hub-wrapper"><div class="gear-layer1"></div><div class="gear-layer2"></div><div class="empty-hub"><div class="display-block"><div class="yowzers"></div>';
                emptyHub += '<h2>This Stream is Empty!</h2><p>The owner is working on it.</p><p>Check back shortly.</p></div></div></div>';
                this.$container.empty().append(emptyHub);
                var count = this.updateTileCount();
                HubsEmbed.Events.trigger('itemsLoaded', {count: count});
                return;
            }
            
            // Check if we have more items to display
            if (!itemData.html.length) { 
                // Update Loading Flag
                this.setLoading(false);
                this.endOfCollection = true;
                return;
            }
            
            // Insert Items into Container
            if (this.$loadingNotifier.length){
                this.$loadingNotifier.before(itemData.html);
            } else {
                this.$container.append(itemData.html);
            }
            var count = this.updateTileCount();
            HubsEmbed.Events.trigger('itemsLoaded', {count: count});
            
            // Resize All Image Tiles
            Hubs.updateImageTiles(this.app, '.tile .img');

            // Run friendly timestamp plugin on new html
            Hubs.setFriendlyTimestamps();
            
            // Update Links
            this.app.overrideLinks();
            
            // Update Sizes and Counts after Adding Items
            HubsEmbed.root.setTimeout($.proxy(function() {
                // Update Sizes to get Last Item Bounds 
                this.updateSizes();
                
                // Increment Current Page
                this.currentPage++;
                
                // Update Load URL for Subsequent Loads
                this.loadUrl = this.app.config.serverUrl.lazyLoader;

                // Update Loading Flag
                this.setLoading(false);
                
                // Check Boundary to Load More Items
                if (initialLoad) {
                    this.checkBoundary(!initialLoad);
                }
                
                if (typeof callback === 'function') {
                    callback();
                }
            }, this), 100);
        }, this));
    } catch (e) {
        // Error Statement
        if (e instanceof Shared.Exception) {
            HubsEmbed.log(e);
        } else {
            HubsEmbed.log({'msg': 'ajax-failed', 'args': {'reason': e}, 'type': Shared.LVL_ERROR});
        }
    }
};

/**
 * Update the Size and Boundary of the Last Item in the List
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.updateSizes = function() {
    // Get Last Item in List
    var $allItems = this.$container.find(this.app.config.lazyloader.elements.item),
        $lastItem = ($allItems.length) ? $($allItems[$allItems.length-1]) : $allItems;
        
    if (!$lastItem.length) { return; }
    
    // Get Number of Items
    this.itemCount = $allItems.length;

    // Get Top Offset of Container
    this.containerOffset = this.app.$appContainer[0].offsetTop;
    
    // Get Size of Viewport
    this.viewportSize = Math.max(document.body.clientHeight, window.innerHeight || 0);
    
    // Get Size of Last Item
    this.itemSize = $lastItem.outerHeight();
    
    // Get Bounds of Last Item
    this.lastItemBounds = this.containerOffset + $lastItem[0].offsetTop;// + (this.itemSize / 2);
};

/**
 * Checks the Boundary of the Last Item in the List and Loads More Items if necessary
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.checkBoundary = function(initialLoad) {
    // Get the ScrollTop for the Container
    var scrollTop = this.$scrollContainer.scrollTop() + this.viewportSize;
    
    // Check if we need to Load More Items
    if (scrollTop > this.lastItemBounds) {
        this.loadItems(initialLoad);
    }
};

/**
 * Toggles the display of the Loading More Indicator
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.loadingNotifier = function() {
    // Build Loading Notifier Element
    if (this.$loadingNotifier === null || !this.$loadingNotifier.length) {
        this.$loadingNotifier = this.app.$appContainer.find(this.app.config.lazyloader.elements.notify);
        if (!this.$loadingNotifier.length) { return; }
    }
    
    // Show/Hide Loading Notifier
    if (this.loadingItems) {
        this.$loadingNotifier.show();
    } else {
        this.$loadingNotifier.hide();
    }
};

/**
 * Set the Loading State for the Loading Notifier 
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.setLoading = function(loading) {
    // Update Loading Flag
    this.loadingItems = loading;
    
    // Update Loading Notifier
    this.loadingNotifier();
};

/**
 * Resets the LazyLoader to begin from the beginning again
 * 
 * @public 
 * @this HubsEmbed.LazyLoader
 * @return undefined
 */
HubsEmbed.LazyLoader.prototype.reset = function() {
    // Get Handles to Container & Loading-Notifier Elements
    this.$container = this.app.$appContainer.find(this.app.itemContainerSelector);
    this.$loadingNotifier = this.app.$appContainer.find(this.app.config.lazyloader.elements.notify);
    if (!this.$container.length) { return; }
    
    this.collectionId     = this.app.config.collection;
    this.loadUrl          = this.app.config.serverUrl.initialLoad;
    this.itemSize         = 0;
    this.itemCount        = 0;
    this.viewportSize     = 0;
    this.lastItemBounds   = 0;
    this.currentPage      = 0;
    this.loadingItems     = false;
    this.endOfCollection  = false;
};
